import React, { MouseEventHandler, ReactElement, useCallback, useRef, useState } from 'react';
import { JourneyMenu } from 'src/common/JourneyMenu';
import { EditorConfirmPanel } from 'src/editor/confirm-panel';
import { DeleteRegularIcon } from 'src/monet/icons';
import { functionNoop } from 'src/utils/function/noop';

export interface DashboardIntegrationsConnectionCardOptionsProps {
  onDisconnectClick: () => void;
  onMenuItemClick?: () => void;
  menuItem?: ReactElement;
  renderMenuOrigin: (onClick: MouseEventHandler) => ReactElement;
}

export const DashboardIntegrationsConnectionCardOptions = ({
  renderMenuOrigin,
  menuItem,
  onDisconnectClick,
  onMenuItemClick,
}: DashboardIntegrationsConnectionCardOptionsProps) => {
  const [showDeletePanel, setShowDeletePanel] = useState(false);

  const settingsMenuOpenRef = useRef(functionNoop);
  const settingsMenuCloseRef = useRef(functionNoop);

  const deleteMenuOpenRef = useRef(functionNoop);
  const deleteMenuCloseRef = useRef(functionNoop);

  const onDisconnectClicked = useCallback(() => {
    setShowDeletePanel(true);
  }, []);

  const onMenuClicked: MouseEventHandler = () => {
    settingsMenuOpenRef.current();
  };

  const onDeletePanelClosed = useCallback(() => {
    setShowDeletePanel(false);
  }, []);

  const onBackClicked = useCallback(() => {
    onDeletePanelClosed();
    setTimeout(() => {
      settingsMenuOpenRef.current();
    }, 50);
  }, [onDeletePanelClosed]);

  const onDeleteConfirmClicked = useCallback(() => {
    onDisconnectClick();
    onDeletePanelClosed();
    deleteMenuCloseRef.current();
  }, [onDeletePanelClosed, onDisconnectClick]);

  const onDeleteDenyClicked = useCallback(() => {
    deleteMenuCloseRef.current();
  }, []);

  if (showDeletePanel) {
    return (
      <EditorConfirmPanel
        placement='right-start'
        hasBackButton={true}
        openRef={deleteMenuOpenRef}
        closeRef={deleteMenuCloseRef}
        onBack={onBackClicked}
        onClose={onDeletePanelClosed}
        copy='Would you like to disconnect this account? You’ll have to reconfigure this integration if you change your
            mind'
        confirmCopy='Disconnect'
        denyCopy='No, keep'
        onConfirm={onDeleteConfirmClicked}
        onDeny={onDeleteDenyClicked}
        renderOrigin={() => renderMenuOrigin(onMenuClicked)}
      />
    );
  }

  return (
    <JourneyMenu
      openRef={settingsMenuOpenRef}
      placement='right-start'
      closeRef={settingsMenuCloseRef}
      renderOrigin={() => renderMenuOrigin(onMenuClicked)}
    >
      <JourneyMenu.Item
        className='text-bedrock-p text-bedrock-black text-left'
        onClick={onMenuItemClick || onDisconnectClicked}
      >
        {menuItem || (
          <div className='flex items-center space-x-sm'>
            <DeleteRegularIcon />
            <span className='text-bedrock-p text-bedrock-black'>Disconnect</span>
          </div>
        )}
      </JourneyMenu.Item>
    </JourneyMenu>
  );
};
